import { createContext, useContext, useId, useLayoutEffect, useRef, type ReactNode } from 'react';

export const CapacityValidationContext = createContext<Record<string, string>>({});

// Composite controls (lookup, TypeAhead, date picker) keep their own input refs.
// Mark their native inputs here so every control exposes the same accessible error.
export function CapacityValidationControl({ name, children }: { name: string; children: ReactNode }) {
  const error = useContext(CapacityValidationContext)[name];
  const ref = useRef<HTMLDivElement>(null);
  const id = useId();
  useLayoutEffect(() => {
    if (!error) return;
    const inputs = ref.current?.querySelectorAll<HTMLElement>('input:not([type="hidden"]), textarea, select, output');
    inputs?.forEach(input => {
      if (error) {
        input.setAttribute('aria-invalid', 'true');
        input.setAttribute('aria-describedby', [input.getAttribute('aria-describedby'), id].filter(Boolean).join(' '));
      }
    });
    return () => inputs?.forEach(input => {
      input.removeAttribute('aria-invalid');
      const remaining = input.getAttribute('aria-describedby')?.split(' ').filter(value => value !== id).join(' ');
      if (remaining) input.setAttribute('aria-describedby', remaining);
      else input.removeAttribute('aria-describedby');
    });
  }, [error, id]);
  return <div ref={ref} tabIndex={error ? -1 : undefined} data-capacity-invalid={error ? 'true' : undefined}>
    <div className={error ? 'rounded-lg ring-1 ring-red-500 [&_input]:!border-red-500 [&_textarea]:!border-red-500 [&_input]:!ring-red-500 [&_textarea]:!ring-red-500' : undefined}>{children}</div>
    {error && <p id={id} className="mt-1 text-xs text-red-600 dark:text-red-400">{error}</p>}
  </div>;
}
